import { useMemo, useState } from 'react';
import Fuse from 'fuse.js';

import { Prettify } from '@/util/type';

type FuseOptions<TSearchData> = Prettify<
  Fuse.IFuseOptions<TSearchData> & {
    limit: number;
    matchAllOnEmptyQuery?: boolean;
  }
>;

export function useFuse<TSearchData>(
  list: TSearchData[],
  options: FuseOptions<TSearchData>,
) {
  const [query, setQuery] = useState('');

  // removing custom options from Fuse options object
  // NOTE: `limit` is actually a `fuse.search` option, but we merge all options for convenience
  const { limit, matchAllOnEmptyQuery, ...fuseOptions } = options;

  // let's memoize the fuse instance for performances
  const fuse = useMemo(() => new Fuse(list, fuseOptions), [list, fuseOptions]);

  // memoize results whenever the query or options change
  const hits = useMemo(
    // if query is empty and `matchAllOnEmptyQuery` is `true` then return all list
    // NOTE: we remap the results to match the return structure of `fuse.search()`
    () =>
      !query && matchAllOnEmptyQuery
        ? list.slice(0, limit < 0 ? list.length : limit).map((item, refIndex) => ({
            item,
            refIndex,
          }))
        : fuse.search(query, { limit }),
    [fuse, limit, matchAllOnEmptyQuery, query, list],
  );

  // still returning `setQuery` for custom handler implementations
  return {
    hits,
    query,
    setQuery,
  };
}
